// Graph discovery hook. Mirrors crates/tres-api/src/handlers/graphs.rs.
//
// NOTE: `GraphInfo` is declared here AND in lib/api.ts, two hand-maintained
// copies of one wire contract. They have to be edited together — adding
// `statements` to only one produced a type error that was luck, not a gate.
// Collapsing them to a single declaration is tracked on the checklist.
//
// Returns the list of named graphs the user can target for read or
// write operations. Always includes the three managed graphs
// (urn:tres:ontology / urn:tres:vocabulary / urn:tres:data).

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useApiSession } from '@/lib/useApiSession';

export type GraphKind = 'ontology' | 'vocabulary' | 'data' | 'catalog' | null;

export interface GraphInfo {
  /** Bracketed canonical IRI form, e.g. `<urn:tres:ontology>`. */
  uri: string;
  /** Bare IRI form, e.g. `urn:tres:ontology`. */
  bare: string;
  /** "ontology" / "vocabulary" / "data" for managed graphs; null otherwise. */
  kind: GraphKind;
  /** True if maintained by the bootstrap step. */
  managed: boolean;
  /** Statements in this graph. Absent when the count could not be taken —
   *  the row shows a dash rather than the list failing over a number. */
  statements?: number;
}

interface GraphsResponse {
  graphs: GraphInfo[];
}

/**
 * Fetches the current named-graph inventory from the API. Cache for
 * 30 s — the list rarely changes within a session, but we want a load
 * to surface as a new graph eventually.
 */
export function useGraphs() {
  const { token, ready } = useApiSession();

  return useQuery<GraphInfo[]>({
    queryKey: ['graphs', token ?? 'anon'],
    enabled: ready,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await apiFetch<GraphsResponse>('/api/v1/graphs', token);
      if (!r.ok) throw new Error(r.message);
      return r.data.graphs;
    },
  });
}

/**
 * Local name of a graph URI for display — the segment after the last `/`,
 * `#`, or `:` (`urn:tres:graph:fms-process` → `fms-process`). Falls back to
 * the full URI if no separator is present.
 */
export function graphLocalName(bare: string): string {
  const parts = bare.split(/[/#:]/).filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1]! : bare;
}

/**
 * Friendly display label for a graph. Managed graphs get human-friendly
 * names; custom graphs render their local name (the full URI is kept as a
 * tooltip by callers that show one).
 */
export function graphDisplayLabel(g: GraphInfo): string {
  // A custom graph can carry a role, and the role's name belongs to the
  // managed graph alone: two entries both reading "Ontology" is how a load
  // was aimed at the wrong one (2026-10-09, the twin). The custom graph keeps
  // its own name, with the role beside it.
  if (!g.managed && g.kind) {
    return `${graphLocalName(g.bare)} (${g.kind} role)`;
  }
  switch (g.kind) {
    case 'ontology':
      return 'Ontology';
    case 'vocabulary':
      return 'Vocabulary';
    case 'data':
      return 'Data';
    case 'catalog':
      return 'Catalog';
    default:
      return graphLocalName(g.bare);
  }
}
